import React, { useEffect, useRef, useState } from 'react';
import { fetchAuditoriaPedidos } from '../../../api/portalCotacoes/portalCotacoesApi';
import { AuditoriaPedidos } from '../../../interfaces/PortalCotacoes';
import { notify } from '../../../shared/feedback/notify';
import '../../../components/modal/planning-comparison/PlanningComparisonModal.css';
import './AuditoriaPropostaModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Pedidos gerados no ERP (14/08/2026, pedido do Victor: "um pedido foi gerado para quais
 * itens, quais valores, quais justificativas?").
 *
 * Os itens vêm do `payloadSnapshot` de cada pedido — **o que foi efetivamente enviado** ao
 * Nasajon —, e não de uma reconstrução a partir das decisões: se a decisão mudasse depois,
 * a reconstrução mentiria sobre o que o fornecedor recebeu.
 *
 * Ao contrário do modal de escolhas, este BUSCA os dados: o pedido é entidade viva (pode
 * ser reenviado, pode receber baixa de recebimento), então o estado atual dele importa.
 */

interface AuditoriaPedidosModalProps {
  cotacaoId: string;
  onFechar: () => void;
}

function moeda(valor: number | null): string {
  if (valor === null) return '—';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function data(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

/**
 * Formata um dia puro (`AAAA-MM-DD`) sem construir `Date`.
 *
 * `new Date('2026-08-19')` é interpretado como meia-noite **UTC** e renderiza 18/08 em
 * Brasília — a mesma armadilha de fuso do `03-code-conventions.md`. A data da chegada é dia
 * de parede, informado por quem marcou: ela não tem hora nem fuso a converter.
 */
function diaPuro(iso: string | null): string {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

const STATUS_LABEL: Record<string, string> = {
  PENDENTE_ENVIO: 'Pendente de envio',
  ENVIADO: 'Enviado',
  ERRO: 'Erro no envio',
};

const AuditoriaPedidosModal: React.FC<AuditoriaPedidosModalProps> = ({ cotacaoId, onFechar }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [dados, setDados] = useState<AuditoriaPedidos | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) onFechar();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onFechar]);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const resposta = await fetchAuditoriaPedidos(cotacaoId);
        if (!ativo) return;
        setDados(resposta);
        setErro(null);
      } catch (error) {
        if (!ativo) return;
        // Modal vazio leria como "nenhum pedido foi gerado" — afirmação sobre o ERP tirada
        // de uma falha de rede.
        setDados(null);
        setErro('Não foi possível carregar os pedidos. Feche e tente de novo; se persistir, acione a TI.');
        notify.errorFrom(error, 'Não foi possível carregar os pedidos gerados.');
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [cotacaoId]);

  return (
    <div className="planning-modal-overlay">
      <div className="planning-modal-content" ref={modalRef}>
        <div className="planning-modal-header">
          <h2>Pedidos Gerados no ERP</h2>
          <button className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        {dados && (
          <div className="planning-modal-metadata">
            <div className="metadata-item">
              <strong>Cotação:</strong> <span>{dados.cotacaoNumero}</span>
            </div>
            <div className="metadata-item">
              <strong>Pedidos:</strong> <span>{dados.pedidos.length}</span>
            </div>
          </div>
        )}

        <div className="planning-modal-body">
          {carregando && <p className="auditoria-proposta-vazio">Carregando os pedidos...</p>}

          {erro && (
            <p className="auditoria-proposta-erro" role="alert">
              {erro}
            </p>
          )}

          {dados && dados.pedidos.length === 0 && (
            <p className="auditoria-proposta-vazio">Nenhum pedido gerado para esta cotação.</p>
          )}

          {/* Um bloco por fornecedor: o ERP recebe um pedido por fornecedor vencedor. */}
          {dados?.pedidos.map((pedido) => (
            <div key={pedido.id} className="auditoria-pedido-bloco">
              <div className="auditoria-pedido-cabecalho">
                <div>
                  <strong>{pedido.fornecedorNome}</strong>
                  <div className="auditoria-proposta-secundario">{pedido.fornecedorCnpj}</div>
                </div>
                <div className="auditoria-pedido-situacao">
                  {STATUS_LABEL[pedido.status] ?? pedido.status}
                  {/*
                    "Simulado" precisa ficar visível: o pedido existe aqui, mas o ERP não o
                    executou de verdade — quem audita não pode concluir que a compra foi
                    feita.
                  */}
                  {pedido.simulado && <span className="auditoria-pedido-simulado">simulado</span>}
                </div>
              </div>

              <div className="auditoria-pedido-meta">
                <div><strong>Referência no ERP:</strong> {pedido.numeroExterno}</div>
                <div><strong>Nº no Nasajon:</strong> {pedido.nasajonPedidoId || '—'}</div>
                <div><strong>Enviado em:</strong> {data(pedido.enviadoEm)}</div>
                <div><strong>Previsão de entrega:</strong> {data(pedido.previsaoDeEntrega)}</div>
                <div><strong>Total:</strong> {moeda(pedido.total)}</div>
              </div>

              {/*
                Chegadas registradas (transversal/at-86). Antes havia um "Recebido em" único
                por pedido, que prometia "o pedido chegou" mesmo quando só metade da cesta
                tinha chegado. A lista mostra quem marcou, quanto e quando — inclusive o que
                foi cancelado, porque auditoria também é o registro do engano e da correção.
              */}
              {(pedido.recebimentos ?? []).length > 0 && (
                <div className="auditoria-pedido-meta">
                  <div><strong>Chegadas registradas:</strong></div>
                  {pedido.recebimentos.map((r) => (
                    <div key={r.id}>
                      {r.descricao ?? 'Peça'}: {r.quantidade} {r.unidade ?? ''} em{' '}
                      {diaPuro(r.recebidoEm)}
                      {r.registradoPor ? ` (por ${r.registradoPor})` : ''}
                      {r.cancelado
                        ? ` - cancelado${r.cancelamentoMotivo ? `: ${r.cancelamentoMotivo}` : ''}`
                        : ''}
                    </div>
                  ))}
                </div>
              )}

              {pedido.ultimoErro && (
                <p className="auditoria-proposta-erro" role="alert">
                  Último erro: {pedido.ultimoErro}
                  {pedido.tentativas > 1 ? ` (${pedido.tentativas} tentativas)` : ''}
                </p>
              )}

              <div className="auditoria-proposta-tabela-wrapper">
                <table className="auditoria-proposta-tabela">
                  <thead>
                    <tr>
                      <th>Peça</th>
                      <th>Qtd.</th>
                      <th>Valor unit.</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedido.itens.map((item) => (
                      <tr key={`${pedido.id}-${item.codigoProduto}`}>
                        <td>
                          <div>{item.descricao}</div>
                          <div className="auditoria-proposta-secundario">{item.codigoProduto}</div>
                          {item.justificativa && (
                            <div className="auditoria-proposta-secundario">
                              Justificativa: {item.justificativa}
                            </div>
                          )}
                          {item.foiMenorPreco === false && !item.justificativa && (
                            <div className="auditoria-proposta-secundario">Acima do menor preço</div>
                          )}
                        </td>
                        <td>
                          {item.quantidade} {item.unidade}
                        </td>
                        <td>{moeda(item.valorUnitario)}</td>
                        <td>{moeda(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/*
            at-11: o frete informado pelo fornecedor fica registrado na cotação e no
            fechamento da cesta, mas **não é lançado no pedido do ERP** — o payload do
            pedido segue com `modalidade_frete: sem_frete`. O texto diz o que é verdade
            sobre estes números, sem sugerir que o sistema ignora frete.
          */}
          {dados && dados.pedidos.length > 0 && (
            <p className="auditoria-proposta-nota">
              Os itens acima são os que foram efetivamente enviados ao ERP. O frete informado pelo fornecedor
              fica registrado na cotação e não é lançado no pedido.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditoriaPedidosModal;
